import React, { useState } from 'react'
import { useTranslation } from 'react-i18next';
import complaientIcon from "../../assets/icons/complaient.png";
import sendIcon from "../../assets/icons/send.png";
import axiosInstance from "../../services/axios";



const ComplaientForm = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    fullName: '',
    mobile: '',
    category: '',
    send_to: 'administration',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const validateForm = () => {
    const errors = {};

    // Validate Full Name
    if (!formData.fullName.trim()) {
      errors.fullName = t('complaints.validation.fullNameRequired') || 'पूर्ण नाव आवश्यक आहे';
    } else if (formData.fullName.trim().length < 3) {
      errors.fullName = t('complaints.validation.fullNameMinLength') || 'पूर्ण नाव किमान 3 अक्षर असणे आवश्यक आहे';
    } else if (!/^[a-zA-Z\s]*$/.test(formData.fullName)) {
      errors.fullName = t('complaints.validation.fullNameInvalid') || 'पूर्ण नाव केवळ अक्षर असणे आवश्यक आहे';
    }

    // Validate Mobile Number
    if (!formData.mobile.trim()) {
      errors.mobile = t('complaints.validation.mobileRequired') || 'मोबाइल नंबर आवश्यक आहे';
    } else if (!/^[0-9]{10}$/.test(formData.mobile.replace(/[\s-]/g, ''))) {
      errors.mobile = t('complaints.validation.mobileInvalid') || 'वैध 10 अंकी मोबाइल नंबर प्रविष्ट करा';
    }

    // Validate Category
    if (!formData.category) {
      errors.category = t('complaints.validation.categoryRequired') || 'श्रेणी निवडा आवश्यक आहे';
    }

    // Validate Message
    if (!formData.message.trim()) {
      errors.message = t('complaints.validation.messageRequired') || 'संदेश आवश्यक आहे';
    } else if (formData.message.trim().length < 10) {
      errors.message = t('complaints.validation.messageMinLength') || 'संदेश किमान 10 अक्षर असणे आवश्यक आहे';
    }

    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');

    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);
    setFieldErrors({});

    try {
      const complaintData = {
        full_name: formData.fullName,
        mobile_number: formData.mobile,
        category: formData.category,
        send_to: formData.send_to,
        message: formData.message,
        language: localStorage.getItem('i18nextLng') || 'en',
      };

      const response = await axiosInstance.post('/complaints/', complaintData);
      
      setSuccessMessage(t('complaints.successMessage') || 'तुमची तक्रार यशस्वीरित्या सादर झाली.');
      setFormData({
        fullName: '',
        mobile: '',
        category: '',
        send_to: 'administration',
        message: ''
      });
      
      console.log('Complaint submitted:', response.data);
    } catch (error) {
      console.error('Error submitting complaint:', error);
      setErrorMessage(error.response?.data?.message || t('complaints.errorMessage') || 'तक्रार सादर करताना त्रुटी आली. कृपया पुन्हा प्रयत्न करा.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border rounded-md p-7 bg-white flex flex-col gap-3">
      {/* Header */}
      <div className="flex gap-3 items-center">
        <img
          className="w-10 h-10 relative"
          src={complaientIcon}
          alt="Document Icon"
        />
        <span className="font-semibold text-2xl max-sm:text-xl w-full h-fit m-0">
          {t('complaints.title') || 'तक्रारी आणि सल्ला'}
        </span>
      </div>

      <p className="font-normal text-base text-[#212121]">
        {t('complaints.description') || 'तक्रार किंवा सूचना सादर करण्यासाठी कृपया खालील फॉर्म भरा. तुमच्या समस्या किंवा सूचना आमच्यासाठी महत्त्वाच्या आहेत.'}
      </p>

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 text-green-700">
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-700">
          {errorMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Full Name and Mobile Number Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              {t('complaints.form.fullName') || 'पूर्ण नाव'} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              placeholder={t('complaints.form.fullNamePlaceholder') || 'तुमचे पूर्ण नाव'}
              className={`w-full px-3 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent ${
                fieldErrors.fullName ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-green-500'
              }`}
            />
            {fieldErrors.fullName && (
              <p className="text-red-500 text-sm mt-1">{fieldErrors.fullName}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              {t('complaints.form.mobileNumber') || 'मोबाइल नंबर'} <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="mobile"
              value={formData.mobile}
              onChange={handleInputChange}
              placeholder={t('complaints.form.mobileNumberPlaceholder') || 'तुमचा मोबाइल नंबर'}
              className={`w-full px-3 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent ${
                fieldErrors.mobile ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-green-500'
              }`}
            />
            {fieldErrors.mobile && (
              <p className="text-red-500 text-sm mt-1">{fieldErrors.mobile}</p>
            )}
          </div>
        </div>

        {/* Category Dropdown */}
        <div>
          <label className="block text-sm font-medium text-[#212121] mb-2">
            {t('complaints.form.category') || 'श्रेणी'} <span className="text-red-500">*</span>
          </label>
          <select
            name="category"
            value={formData.category}
            onChange={handleInputChange}
            className={`w-full px-3 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent appearance-none bg-white ${
              fieldErrors.category ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-green-500'
            }`}
          >
            <option value="">{t('complaints.form.categoryPlaceholder') || 'श्रेणी निवडा'}</option>
            <option value="water">{t('complaints.form.categories.water') || 'पाणी पुरवठा'}</option>
            <option value="electricity">{t('complaints.form.categories.electricity') || 'वीज'}</option>
            <option value="roads">{t('complaints.form.categories.roads') || 'रस्ते आणि पायाभूत सुविधा'}</option>
            <option value="sanitation">{t('complaints.form.categories.sanitation') || 'स्वच्छता'}</option>
            <option value="healthcare">{t('complaints.form.categories.healthcare') || 'आरोग्यसेवा'}</option>
            <option value="education">{t('complaints.form.categories.education') || 'शिक्षण'}</option>
            <option value="other">{t('complaints.form.categories.other') || 'इतर'}</option>
          </select>
          {fieldErrors.category && (
            <p className="text-red-500 text-sm mt-1">{fieldErrors.category}</p>
          )}
        </div>

        {/* Message Textarea */}
        <div>
          <label className="block text-sm font-medium text-[#212121] mb-2">
            {t('complaints.form.message') || 'संदेश'} <span className="text-red-500">*</span>
          </label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            placeholder={t('complaints.form.messagePlaceholder') || 'तुमची तक्रार किंवा सूचना तपशीलवार वर्णन करा'}
            rows={4}
            maxLength={200}
            className={`w-full px-3 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent resize-none ${
              fieldErrors.message ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-green-500'
            }`}
          />
          <div className="flex justify-between items-center mt-1">
            <div className="text-right text-sm text-gray-500">
              {formData.message.length}/200
            </div>
            {fieldErrors.message && (
              <p className="text-red-500 text-sm">{fieldErrors.message}</p>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-[var(--color-primary)] text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          <img
          className="w-4 h-4 relative"
          src={sendIcon}
          alt="Send Icon"
            />
          {loading ? (t('complaints.form.submitting') || 'सादर करत आहे...') : (t('complaints.form.submitButton') || 'तक्रार सादर करा')}
        </button>
      </form>
    </div>
  )
}

export default ComplaientForm